import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Button, Input, Select, Space, Tooltip } from "antd";

// Simple debounce to limit API calls while typing
const debounce = (fn, delay = 300) => {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), delay);
  };
};

/**
 * Contrôles réutilisables pour les listes : recherche, filtres, export.
 * - N'émet que les paramètres (aucun fetch ici) pour réutiliser les requêtes existantes côté page.
 * - Débounce la recherche/filtre pour éviter de spammer le backend.
 * - Garde-fous sur les données pour éviter les crashs si le backend renvoie des valeurs inattendues.
 */
const ListControls = ({
  filters = [],
  onParamsChange,
  exportFormats = [],
  isLoading = false,
  searchPlaceholder = "Rechercher...",
  actions,
}) => {
  const [search, setSearch] = useState("");
  const [filterValues, setFilterValues] = useState({});

  const debouncedEmit = useMemo(
    () =>
      debounce((payload) => {
        if (typeof onParamsChange === "function") {
          onParamsChange(payload);
        }
      }, 300),
    [onParamsChange]
  );

  const handleSearchChange = (value) => {
    const nextSearch = value || "";
    setSearch(nextSearch);
    debouncedEmit({ search: nextSearch, filters: { ...filterValues } });
  };

  const handleFilterChange = (key, value) => {
    const nextFilters = { ...filterValues, [key]: value || undefined };
    setFilterValues(nextFilters);
    debouncedEmit({ search, filters: nextFilters });
  };

  const safeOptions = (options) =>
    Array.isArray(options)
      ? options.map((opt) => ({
          value: opt?.value ?? opt?.id ?? opt,
          label: opt?.label ?? opt?.name ?? String(opt ?? ""),
        }))
      : [];

  return (
    <Space style={{ marginBottom: 16 }} wrap>
      <Input.Search
        allowClear
        placeholder={searchPlaceholder}
        value={search}
        onChange={(e) => handleSearchChange(e.target.value)}
        onSearch={(value) => handleSearchChange(value)}
        style={{ minWidth: 220 }}
        loading={isLoading}
      />

      {filters.map((filter) => (
        <Select
          key={filter.key}
          allowClear
          placeholder={filter.label || filter.key}
          value={filterValues[filter.key]}
          options={safeOptions(filter.options)}
          onChange={(value) => handleFilterChange(filter.key, value)}
          style={{ minWidth: 180 }}
          loading={isLoading}
        />
      ))}

      {exportFormats.length > 0 ? (
        exportFormats.map((fmt) => (
          <Tooltip
            title={fmt.tooltip || `Exporter en ${fmt.label || fmt.key}`}
            key={fmt.key}
          >
            <Button
              onClick={fmt.onExport}
              loading={isLoading}
              disabled={isLoading || typeof fmt.onExport !== "function"}
            >
              {fmt.label || "Exporter"}
            </Button>
          </Tooltip>
        ))
      ) : (
        <Button disabled>Exporter</Button>
      )}

      {actions}
    </Space>
  );
};

ListControls.propTypes = {
  filters: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string,
      options: PropTypes.array,
    })
  ),
  onParamsChange: PropTypes.func,
  exportFormats: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string,
      onExport: PropTypes.func,
      tooltip: PropTypes.string,
    })
  ),
  isLoading: PropTypes.bool,
  searchPlaceholder: PropTypes.string,
  actions: PropTypes.node,
};

export default ListControls;
